import React, { useState } from 'react';
import { useDroppable } from '@dnd-kit/core';
import { Box, Tooltip } from '@mui/material';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import DashboardCustomizeOutlinedIcon from '@mui/icons-material/DashboardCustomizeOutlined';
import LayersOutlinedIcon from '@mui/icons-material/LayersOutlined';
import PhotoLibraryOutlinedIcon from '@mui/icons-material/PhotoLibraryOutlined';
import TuneOutlinedIcon from '@mui/icons-material/TuneOutlined';
import ViewCarouselOutlinedIcon from '@mui/icons-material/ViewCarouselOutlined';
import WidgetsOutlinedIcon from '@mui/icons-material/WidgetsOutlined';
import { LibraryCatalogKind } from '../libraryCatalog';
import { LibraryIntent, SlideObject, VisibleAsset } from '../types';
import { PRES } from '../chrome';
import { focusVisibleSx } from '../ui/focus';
import PropertiesPanel from './PropertiesPanel';
import LayersPanel from './LayersPanel';
import AssetBrowser from './AssetBrowser';
import CommentsPanel from './CommentsPanel';
import GlobalComponentsPanel from './GlobalComponentsPanel';
import PresentationAiPanel from './PresentationAiPanel';
import { PresentationComment, PresentationGlobalComponent } from '../types';
import { ListKind } from './listHtml';
import { hasEditableText } from './shapeFrame';
import { TextStyleStep } from './layerList';
import { MAX_LIBRARY_WIDTH, columnsForWidth, widthForColumns } from './assetBrowserLayout';
import TemplatePickerPanel from './TemplatePickerPanel';
import LibraryPickerPanel from './LibraryPickerPanel';
import { PresentationLibrarySlide, PresentationTemplate } from '../types';
import { COPY } from '../presentationCopy';
import { SLIDE_LIBRARY_TAB_DROP_ID } from './slideLibraryDrop';

export type EditorDockTab = 'properties' | 'layers' | 'library' | 'templates' | 'slideLibrary' | 'comments' | 'components' | 'ai';

const DOCK_WIDTH_KEY = 'athena.presentations.dockWidth';
const MIN_DOCK_WIDTH = 280;

function readDockWidth(): number {
  try {
    const stored = Number(window.localStorage.getItem(DOCK_WIDTH_KEY));
    if (stored >= MIN_DOCK_WIDTH && stored <= MAX_LIBRARY_WIDTH) return stored;
  } catch {
    return 300;
  }
  return 300;
}

interface EditorDockProps {
  tab: EditorDockTab;
  onTab: (tab: EditorDockTab) => void;
  advancedMode: boolean;
  objects: SlideObject[];
  selectedObject: SlideObject | null;
  selectedObjectIds?: string[];
  slideBackground: string;
  onSelect: (id: string, options?: { additive?: boolean; isolate?: boolean }) => void;
  onChangeObject: (patch: Partial<SlideObject>) => void;
  onChangeObjects: (objects: SlideObject[]) => void;
  onStyleObject?: (patch: Record<string, unknown>) => void;
  onSlideBackground: (color: string) => void;
  onOpenAssets: () => void;
  onPlaceAsset: (asset: VisibleAsset, intent: LibraryIntent, meta?: { catalogKind?: LibraryCatalogKind | null }) => void;
  libraryIntent?: LibraryIntent;
  revealAssetId?: string | null;
  revealNonce?: number;
  onToggleList?: (kind: ListKind) => void;
  onApplyTextStyle?: (step: TextStyleStep) => void;
  lockedCategoryName?: string | null;
  comments?: PresentationComment[];
  canWriteComment?: boolean;
  canResolveComment?: boolean;
  onCreateComment?: (body: string) => Promise<void>;
  onResolveComment?: (id: string) => Promise<void>;
  globalComponents?: PresentationGlobalComponent[];
  selectedObjects?: SlideObject[];
  canManageGlobals?: boolean;
  onSaveGlobal?: (name: string) => Promise<void>;
  onPlaceGlobal?: (id: string) => Promise<void>;
  onDeleteGlobal?: (id: string) => Promise<void>;
  aiEnabled?: boolean;
  aiBusy?: boolean;
  onAskAi?: (instruction: string) => Promise<boolean>;
  aiChatMessages?: import('./PresentationAiPanel').PresentationAiChatMessage[];
  onAiChat?: (message: string) => Promise<boolean>;
  onInsertAiChat?: (content: string) => boolean;
  onClearAiChat?: () => void;
  onSetLinkedField?: (key: string, source: 'direct' | 'local') => void;
  onDetachGlobal?: () => void;
  canDetachGlobal?: boolean;
  templateMode?: boolean;
  libraryMode?: boolean;
  onFieldBinding?: (binding: import('../types').FieldBinding | undefined, placeholder?: string) => void;
  templates?: PresentationTemplate[];
  librarySlides?: PresentationLibrarySlide[];
  pageFormat?: string | null;
  onAddSlideFromLayout?: (layoutId: string, templateId: string) => void;
  onAddSlideFromLibrary?: (librarySlideId: string) => void;
  addingLayout?: boolean;
  layoutPickerDisabled?: boolean;
  canManageTemplates?: boolean;
  onEditTemplate?: (templateId: string, layoutId?: string, fromName?: string) => void;
  onDuplicateTemplate?: (templateId: string) => Promise<void>;
  onRenameTemplate?: (templateId: string, name: string) => Promise<void>;
  onArchiveTemplate?: (templateId: string) => Promise<void>;
  onCreateTemplate?: (name: string) => Promise<void>;
  onDuplicateLayout?: (templateId: string, layoutId: string) => Promise<void>;
  onCopyLayoutToKit?: (templateId: string, layoutId: string) => void;
  onMoveLayoutToKit?: (templateId: string, layoutId: string) => void;
  canSaveToLibrary?: boolean;
}

const DockTab: React.FC<{
  id: EditorDockTab;
  label: string;
  icon: React.ReactNode;
  active: boolean;
  dropId?: string;
  onClick: () => void;
}> = ({ id, label, icon, active, dropId, onClick }) => {
  const { setNodeRef, isOver } = useDroppable({
    id: dropId || `dock-tab-${id}`,
    disabled: !dropId,
  });
  return (
    <Tooltip title={label} arrow>
      <Box
        ref={setNodeRef}
        component="button"
        type="button"
        role="tab"
        aria-label={label}
        aria-selected={active}
        onClick={onClick}
        sx={{
          flex: '1 1 0',
          minWidth: 32,
          height: 40,
          p: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          border: 0,
          bgcolor: isOver || active ? PRES.surfaceSunken : 'transparent',
          color: active || isOver ? PRES.interactive : PRES.textSecondary,
          borderBottom: active || isOver ? `2px solid ${PRES.interactive}` : '2px solid transparent',
          '&:hover': { bgcolor: PRES.surfaceSunken, color: PRES.textPrimary },
          ...focusVisibleSx,
        }}
      >
        {icon}
      </Box>
    </Tooltip>
  );
};

const EditorDock: React.FC<EditorDockProps> = ({
  tab,
  onTab,
  advancedMode,
  objects,
  selectedObject,
  selectedObjectIds,
  slideBackground,
  onSelect,
  onChangeObject,
  onChangeObjects,
  onStyleObject,
  onSlideBackground,
  onOpenAssets,
  onPlaceAsset,
  libraryIntent,
  revealAssetId,
  revealNonce,
  onToggleList,
  onApplyTextStyle,
  lockedCategoryName,
  comments = [],
  canWriteComment,
  canResolveComment,
  onCreateComment,
  onResolveComment,
  globalComponents = [],
  selectedObjects = [],
  canManageGlobals,
  onSaveGlobal,
  onPlaceGlobal,
  onDeleteGlobal,
  aiEnabled,
  aiBusy,
  onAskAi,
  aiChatMessages,
  onAiChat,
  onInsertAiChat,
  onClearAiChat,
  onSetLinkedField,
  onDetachGlobal,
  canDetachGlobal,
  templateMode,
  libraryMode,
  onFieldBinding,
  templates = [],
  librarySlides = [],
  pageFormat,
  onAddSlideFromLayout,
  onAddSlideFromLibrary,
  addingLayout,
  layoutPickerDisabled,
  canManageTemplates,
  onEditTemplate,
  onDuplicateTemplate,
  onRenameTemplate,
  onArchiveTemplate,
  onCreateTemplate,
  onDuplicateLayout,
  onCopyLayoutToKit,
  onMoveLayoutToKit,
  canSaveToLibrary = false,
}) => {
  const [width, setWidth] = useState(readDockWidth);
  const specialMode = Boolean(templateMode || libraryMode);
  const tabs: Array<{ id: EditorDockTab; label: string; icon: React.ReactNode }> = [
    { id: 'properties', label: 'Egenskaper', icon: <TuneOutlinedIcon sx={{ fontSize: 19 }} /> },
    ...(!specialMode ? [{ id: 'comments' as const, label: 'Kommentarer', icon: <ChatBubbleOutlineIcon sx={{ fontSize: 19 }} /> }] : []),
    ...(advancedMode ? [
      { id: 'layers' as const, label: 'Lager', icon: <LayersOutlinedIcon sx={{ fontSize: 19 }} /> },
      ...(!specialMode ? [{ id: 'components' as const, label: 'Komponenter', icon: <WidgetsOutlinedIcon sx={{ fontSize: 19 }} /> }] : []),
    ] : []),
    { id: 'library', label: 'Bibliotek', icon: <PhotoLibraryOutlinedIcon sx={{ fontSize: 19 }} /> },
    ...(!specialMode ? [{ id: 'templates' as const, label: 'Mallar', icon: <DashboardCustomizeOutlinedIcon sx={{ fontSize: 19 }} /> }] : []),
    ...(!specialMode ? [{ id: 'slideLibrary' as const, label: COPY.slideLibrary, icon: <ViewCarouselOutlinedIcon sx={{ fontSize: 19 }} /> }] : []),
    ...(!specialMode && aiEnabled ? [{ id: 'ai' as const, label: 'AI', icon: <AutoAwesomeOutlinedIcon sx={{ fontSize: 19 }} /> }] : []),
  ];

  const startResize = (event: React.PointerEvent<HTMLDivElement>) => {
    event.preventDefault();
    const startX = event.clientX;
    const startWidth = width;
    const onMove = (moveEvent: PointerEvent) => {
      setWidth(Math.min(MAX_LIBRARY_WIDTH, Math.max(MIN_DOCK_WIDTH, startWidth + (startX - moveEvent.clientX))));
    };
    const onUp = (upEvent: PointerEvent) => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      const raw = Math.min(MAX_LIBRARY_WIDTH, Math.max(MIN_DOCK_WIDTH, startWidth + (startX - upEvent.clientX)));
      const next = tab === 'library' || tab === 'templates' || tab === 'slideLibrary' ? widthForColumns(columnsForWidth(raw)) : raw;
      setWidth(next);
      try { window.localStorage.setItem(DOCK_WIDTH_KEY, String(next)); } catch { /* ignore */ }
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
  };

  return (
    <Box sx={{
      width,
      bgcolor: PRES.white,
      borderLeft: `1px solid ${PRES.line}`,
      display: 'flex',
      flexDirection: 'column',
      minHeight: 0,
      flexShrink: 0,
      position: 'relative',
    }}>
      <Box
        onPointerDown={startResize}
        title="Dra för att ändra bredd"
        sx={{
          position: 'absolute',
          left: 0,
          top: 0,
          bottom: 0,
          width: 14,
          cursor: 'ew-resize',
          zIndex: 4,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          '&:hover .dock-handle, &:active .dock-handle': { bgcolor: PRES.gold, opacity: 1 },
        }}
      >
        <Box
          className="dock-handle"
          sx={{
            width: 4,
            height: 64,
            borderRadius: 99,
            bgcolor: PRES.navy,
            opacity: 0.38,
            pointerEvents: 'none',
          }}
        />
      </Box>
      <Box role="tablist" aria-label="Paneler" sx={{ display: 'flex', borderBottom: `1px solid ${PRES.line}`, flexShrink: 0 }}>
        {tabs.map((item) => (
          <DockTab
            key={item.id}
            id={item.id}
            label={item.label}
            icon={item.icon}
            active={tab === item.id}
            dropId={item.id === 'slideLibrary' && canSaveToLibrary ? SLIDE_LIBRARY_TAB_DROP_ID : undefined}
            onClick={() => onTab(item.id)}
          />
        ))}
      </Box>
      <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto', display: 'flex', flexDirection: 'column' }}>
        {tab === 'properties' && (
          <PropertiesPanel
            embedded
            object={selectedObject}
            advancedMode={advancedMode}
            slideBackground={slideBackground}
            onChange={onChangeObject}
            onStyle={onStyleObject}
            onSlideBackground={onSlideBackground}
            onOpenAssets={onOpenAssets}
            onToggleList={onToggleList}
            onApplyTextStyle={onApplyTextStyle}
            onSetLinkedField={onSetLinkedField}
            slideObjects={objects}
            onDetachGlobal={onDetachGlobal}
            canDetachGlobal={canDetachGlobal}
            templateMode={templateMode}
            onFieldBinding={onFieldBinding}
          />
        )}
        {tab === 'comments' && onCreateComment && onResolveComment && (
          <CommentsPanel
            comments={comments}
            canWrite={Boolean(canWriteComment)}
            canResolve={Boolean(canResolveComment)}
            onCreate={onCreateComment}
            onResolve={onResolveComment}
          />
        )}
        {tab === 'components' && advancedMode && onSaveGlobal && onPlaceGlobal && onDeleteGlobal && (
          <GlobalComponentsPanel
            components={globalComponents}
            selectedObjects={selectedObjects}
            canManage={Boolean(canManageGlobals)}
            canPlace={Boolean(onPlaceGlobal)}
            onSave={onSaveGlobal}
            onPlace={onPlaceGlobal}
            onDelete={onDeleteGlobal}
          />
        )}
        {tab === 'ai' && onAskAi && (
          <PresentationAiPanel
            enabled={Boolean(aiEnabled)}
            busy={aiBusy}
            hasSelectedText={Boolean(selectedObject && hasEditableText(selectedObject))}
            onAsk={onAskAi}
            chatMessages={aiChatMessages}
            onChat={onAiChat}
            onInsertChat={onInsertAiChat}
            onClearChat={onClearAiChat}
          />
        )}
        {tab === 'layers' && advancedMode && (
          <LayersPanel
            objects={objects}
            selectedObjectId={selectedObject?.id || null}
            selectedObjectIds={selectedObjectIds}
            onSelect={onSelect}
            onReorder={onChangeObjects}
            onRename={(id, name) => {
              onSelect(id);
              onChangeObjects(objects.map((object) => object.id === id ? { ...object, name } : object));
            }}
          />
        )}
        {tab === 'library' && (
          <AssetBrowser
            embedded
            open
            panelWidth={width}
            onClose={() => onTab('properties')}
            intent={libraryIntent}
            lockedCategoryName={lockedCategoryName}
            revealAssetId={revealAssetId}
            revealNonce={revealNonce}
            onPlace={onPlaceAsset}
          />
        )}
        {tab === 'slideLibrary' && !specialMode && onAddSlideFromLibrary && (
          <LibraryPickerPanel
            items={librarySlides}
            pageFormat={pageFormat}
            panelWidth={width}
            busy={addingLayout}
            disabled={layoutPickerDisabled}
            canSaveToLibrary={canSaveToLibrary}
            onAddPage={onAddSlideFromLibrary}
          />
        )}
        {tab === 'templates' && !templateMode && onAddSlideFromLayout && (
          <TemplatePickerPanel
            templates={templates}
            pageFormat={pageFormat}
            panelWidth={width}
            busy={addingLayout}
            disabled={layoutPickerDisabled}
            canManage={canManageTemplates}
            onAddPage={onAddSlideFromLayout}
            onEdit={onEditTemplate}
            onDuplicate={onDuplicateTemplate}
            onRename={onRenameTemplate}
            onArchive={onArchiveTemplate}
            onCreate={onCreateTemplate}
            onDuplicateLayout={onDuplicateLayout}
            onCopyLayoutToKit={onCopyLayoutToKit}
            onMoveLayoutToKit={onMoveLayoutToKit}
          />
        )}
      </Box>
    </Box>
  );
};

export default EditorDock;
